import React, { useEffect, useRef, useState } from 'react'
import { useForm, FormProvider, useFormContext } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Grid, Box, Divider, Typography, Chip, IconButton, TextField, MenuItem, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@material-ui/core'
import { components } from 'cng-web-lib'
import { useParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import pathMap from 'src/paths/pathMap'
import ConfirmDialog from 'src/views/common/ConfirmDialog'
import MPCIApiUrls from '../Common/services/MPCIApiService'
import MpciApiClient from '../Common/services/MpciApiClient'
import UaeMpciApiUrls from 'src/apiUrls/UaeMpciApiUrls'
import FormProperties from './FormProperties'
import makeValidationSchema from './MakeValidationSchema'
import ParentCard from 'src/components/era-package/shared/ParentCard'
import SnackBar from 'src/components/era-package/era/SnackBar'
import CngSection from 'src/components/cngcomponents/CngSection'
import AuditHistoryTable from '../Common/components/AuditHistoryTable'
import { STATUSES_REQUIRE_ATTACHMENT, STATUSES_UPDATE_MODE, getStatusLabel, STATUS_CODES, SUB_STATUS_CODES } from '../Common/constants/billStatuses'

const isUpdateModeStatus = (status) => STATUSES_UPDATE_MODE.includes(status)

const {
  button: { CngButton }
} = components

function humanizeKey(key) {
  if (/^\d+$/.test(key)) return `#${parseInt(key, 10) + 1}`
  return key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim()
}
function flattenFormErrors(errors, acc = [], path = '') {
  for (const [key, val] of Object.entries(errors || {})) {
    const seg = humanizeKey(key)
    const currentPath = path ? `${path} > ${seg}` : seg
    if (val?.message) acc.push(`${currentPath}: ${val.message}`)
    else if (val && typeof val === 'object') flattenFormErrors(val, acc, currentPath)
  }
  return acc
}

function EditPage({ history, showNotification }) {
  const { id } = useParams()
  const [isFetching, setIsFetching] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [showError, setShowError] = useState(null)
  const [recordStatus, setRecordStatus] = useState('')
  const [recordSubStatus, setRecordSubStatus] = useState('')
  const [recordIsEdit, setRecordIsEdit] = useState(null)
  const [supportingFiles, setSupportingFiles] = useState([])
  const fileInputRef = useRef(null)
  const [submitWarningOpen, setSubmitWarningOpen] = useState(false)
  const [submitWarnings, setSubmitWarnings] = useState([])
  const [draftWarningOpen, setDraftWarningOpen] = useState(false)
  const [draftWarnings, setDraftWarnings] = useState([])
  const [submitMode, setSubmitMode] = useState('update_filing')
  const [fileUploadErrors, setFileUploadErrors] = useState([])

  const isSubmErrFirstDraft = recordStatus === STATUS_CODES.SUBM_ERR &&
    (recordSubStatus ? recordSubStatus === SUB_STATUS_CODES.FD : recordIsEdit !== 'true')
  const isUpdateMode = isUpdateModeStatus(recordStatus) && !isSubmErrFirstDraft
  const isDraft = [STATUS_CODES.DRAFT, STATUS_CODES.UPDATE_DRAFT].includes(recordStatus) || isSubmErrFirstDraft
  const [isOnLastStep, setIsOnLastStep] = useState(false)

  const methods = useForm({
    defaultValues: { ...FormProperties.formikProps.initialValues },
    resolver: yupResolver(makeValidationSchema())
  })

  useEffect(() => {
    MpciApiClient.getById(
      UaeMpciApiUrls.HOUSE_BILL_GET,
      id,
      (record) => {
        if (!record) {
          showNotification('error', 'Data not found.')
          history.push(pathMap.UAE_MPCI_HOUSE_BILL_LIST_VIEW)
                } else {
          if (record.status === 'SUBM') {
            history.replace(`${pathMap.UAE_MPCI_HOUSE_BILL}/view/${id}`)
            return
          }
          methods.reset(FormProperties.toClientDataFormat(record))
          setRecordStatus(record.status || '')
          setRecordSubStatus(record.subStatus || '')
          setRecordIsEdit(record.isEdit || null)
        }
        setIsFetching(true)
      },
      () => {
        showNotification('error', 'Failed to load record.')
        history.push(pathMap.UAE_MPCI_HOUSE_BILL_LIST_VIEW)
        setIsFetching(true)
      }
    )
  }, [])

  const executeSaveDraft = () => {
    setSubmitting(true)
    const payload = FormProperties.toServerDataFormat(methods.getValues())
    MpciApiClient.putById(
      MPCIApiUrls.HOUSE_BILL.PUT,
      id,
      payload,
      [],
      (savedData) => {
        setSubmitting(false)
        if (savedData) {
          methods.reset(FormProperties.toClientDataFormat(savedData))
          setRecordStatus(savedData.status || recordStatus)
        }
        setShowSuccess(true)
      },
      (err) => {
        setSubmitting(false)
        const errMsg = (err && err.status && err.message && err.status !== 500) ? `Error: ${err.status} - ${err.message}` : 'Failed to update House Bill. Please try again.'
        setShowError(errMsg)
      }
    )
  }

  const handleSaveDraft = () => {
    const warnings = FormProperties.validateForSubmit(methods.getValues())
    if (warnings.length > 0) {
      setDraftWarnings(warnings)
      setDraftWarningOpen(true)
      return
    }
    executeSaveDraft()
  }

  const handleSubmitUpdate = methods.handleSubmit((formValues) => {
    if (submitMode === 'supporting_docs_only' && supportingFiles.length === 0) {
      showNotification('error', 'At least one supporting document is required.')
      return
    }
    setSubmitting(true)
    const payload = FormProperties.toServerDataFormat(formValues)
    payload.updateSuppDocOnly = submitMode === 'supporting_docs_only'
    if (submitMode === 'supporting_docs_only') {
      payload.functionType = null
      payload.reasonForUpdatingFiling = null
    }
    MpciApiClient.putById(
      UaeMpciApiUrls.HOUSE_BILL_SUBMIT,
      id,
      payload,
      supportingFiles,
      () => {
        setSubmitting(false)
        showNotification('success', 'House Bill update submitted to NAIC successfully.')
        history.push(pathMap.UAE_MPCI_HOUSE_BILL_LIST_VIEW)
      },
      (err) => {
        setSubmitting(false)
        const submitErrMsg = (err && err.status && err.message && err.status !== 500) ? `Error: ${err.status} - ${err.message}` : 'Submit update failed. Please try again.'
        showNotification('error', submitErrMsg)
      }
    )
  }, (errors) => {
    const messages = flattenFormErrors(errors)
    setSubmitWarnings(messages.length > 0 ? messages : ['Please fix all required fields before submitting.'])
    setSubmitWarningOpen(true)
  })

  function handlePreSubmitUpdate() {
    const values = methods.getValues()
    const warnings = []
    if (isUpdateMode) {
      if (submitMode === 'supporting_docs_only') {
        if (supportingFiles.length === 0) warnings.push('At least one supporting document must be uploaded.')
      } else {
        const validUpdateTypes = UPDATE_FUNCTION_TYPES.map(t => t.value)
        if (!validUpdateTypes.includes(values.functionType)) warnings.push('Update Filing: Function Type is required before submitting.')
        if (['CHG', 'SWB', 'RIJ'].includes(values.functionType) && !values.reasonForUpdatingFiling) warnings.push('Update Filing: Reason for Updating Filing is required for the selected Function Type.')
      }
    }
    warnings.push(...FormProperties.validateForSubmit(values))
    if (warnings.length > 0) {
      setSubmitWarnings(warnings)
      setSubmitWarningOpen(true)
      return
    }
    handleSubmitUpdate()
  }

  function handleFileChange(e) {
    const chosen = Array.from(e.target.files || [])
    const errors = []
    const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png']
    const ALLOWED_EXTS = /\.(pdf|jpg|jpeg|png)$/i
    const MAX_SIZE_BYTES = 10 * 1024 * 1024

    const valid = []
    chosen.forEach((file) => {
      if (!ALLOWED_TYPES.includes(file.type) && !ALLOWED_EXTS.test(file.name)) {
        errors.push(`"${file.name}" is not a supported format. Only PDF, JPG, JPEG, PNG allowed.`)
        return
      }
      if (file.size > MAX_SIZE_BYTES) {
        errors.push(`"${file.name}" exceeds the 10 MB maximum file size.`)
        return
      }
      valid.push(file)
    })

    setSupportingFiles((prev) => {
      const combined = [...prev, ...valid]
      if (combined.length > 5) {
        errors.push('Maximum 5 files allowed. Extra files were not added.')
        return combined.slice(0, 5)
      }
      return combined
    })
    setFileUploadErrors(errors)
    e.target.value = ''
  }

  function removeFile(index) {
    setSupportingFiles((prev) => prev.filter((_, i) => i !== index))
  }

  if (!isFetching) return <p>Loading...</p>

    return (
    <ParentCard title={isUpdateMode && !isDraft ? 'Update House Bill of Lading' : isDraft ? 'Edit House Bill of Lading Draft' : 'Edit House Bill of Lading'}>
      <FormProvider {...methods}>
        <form>
          {isUpdateMode && (
            <UpdateFilingBanner
              recordStatus={recordStatus}
              submitMode={submitMode}
              setSubmitMode={setSubmitMode}
              files={supportingFiles}
              onFileChange={handleFileChange}
              onRemove={removeFile}
              fileInputRef={fileInputRef}
              fileUploadErrors={fileUploadErrors}
              onClearFileErrors={() => setFileUploadErrors([])}
            />
          )}

          {/* Filing Reference — always shown in edit mode */}
          <FilingReferenceSection recordStatus={recordStatus} billType='HBL' systemId={id} />

          <Box mt={2}>
            <FormProperties.Fields
              disabled={submitMode === 'supporting_docs_only'}
              showNotification={showNotification}
              shouldHideMap={{}}
              isCreateMode={false}
              onActiveStepChange={(step, isLast) => setIsOnLastStep(isLast)}
            />
          </Box>

          {recordStatus !== STATUS_CODES.DRAFT && (
            <Box mt={3}>
              <Divider />
              <Box mt={3}>
                <CngSection title='Audit History'>
                  <AuditHistoryTable id={id} />
                </CngSection>
              </Box>
            </Box>
          )}

          <Box mt={2}>
            <EditPageButtonSection
              history={history}
              loading={submitting}
              isUpdateMode={isUpdateMode}
              recordStatus={recordStatus}
              onSaveDraft={handleSaveDraft}
              onSubmitUpdate={handlePreSubmitUpdate}
              showSubmit={isOnLastStep}
            />
          </Box>
        </form>
      </FormProvider>
      <SnackBar
        title='Update Successful'
        type='success'
        open={showSuccess}
        onClose={(e, reason) => {
          if (reason === 'timeout') {
            setShowSuccess(false)
            history.push(pathMap.UAE_MPCI_HOUSE_BILL_LIST_VIEW)
          }
        }}
        duration={3000}
      >
        House Bill updated successfully. Redirecting...
      </SnackBar>
      <SnackBar
        title='Update Failed'
        type='error'
        open={Boolean(showError)}
        onClose={() => setShowError(null)}
        duration={8000}
      >
        {showError || 'Failed to update House Bill. Please try again.'}
      </SnackBar>
      <Dialog open={submitWarningOpen} onClose={() => setSubmitWarningOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>{isUpdateMode ? 'Submit Update' : 'Submit to NAIC'}</DialogTitle>
        <DialogContent>
          <Typography variant='body2' style={{ marginBottom: 8 }}>Please fix the following issues before submitting:</Typography>
          <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
            {submitWarnings.map((w, i) => <li key={i}><Typography variant='body2'>{w}</Typography></li>)}
          </ul>
        </DialogContent>
        <DialogActions>
          <CngButton color='secondary' onClick={() => setSubmitWarningOpen(false)}>Cancel</CngButton>
        </DialogActions>
      </Dialog>
      <Dialog open={draftWarningOpen} onClose={() => setDraftWarningOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>Save as Draft</DialogTitle>
        <DialogContent>
          <Typography variant='body2' style={{ marginBottom: 8 }}>The following required fields are not yet filled. You can still save as draft and complete them later.</Typography>
          <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
            {draftWarnings.map((w, i) => <li key={i}><Typography variant='body2'>{w}</Typography></li>)}
          </ul>
        </DialogContent>
        <DialogActions>
          <CngButton color='secondary' onClick={() => setDraftWarningOpen(false)}>Cancel</CngButton>
          <CngButton onClick={() => { setDraftWarningOpen(false); executeSaveDraft() }}>OK, Save Draft</CngButton>
        </DialogActions>
      </Dialog>
    </ParentCard>
  )
}

export default EditPage

function EditPageButtonSection({ history, loading, isUpdateMode, recordStatus, onSaveDraft, onSubmitUpdate, showSubmit }) {
  const [confirmDialog, setConfirmDialog] = useState(false)

  return (
    <>
      <Grid container justify='flex-end' spacing={2}>
        {/* Cancel */}
        <Grid item xs='auto'>
          <CngButton
            color='secondary'
            disabled={loading}
            onClick={() => setConfirmDialog(true)}
            size='medium'
            startIcon={<FontAwesomeIcon icon={['fal', 'arrow-left']} />}
          >
            Cancel
          </CngButton>
        </Grid>

        {/* Save Draft */}
        <Grid item xs='auto'>
          <CngButton
            color='secondary'
            disabled={loading}
            onClick={onSaveDraft}
            size='medium'
            shouldShowProgress={loading}
            startIcon={<FontAwesomeIcon icon={['fal', 'floppy-disk']} />}
          >
            Save Draft
          </CngButton>
        </Grid>

        {/* Primary action — only on last step */}
        {showSubmit && (
          <Grid item xs='auto'>
            {isUpdateMode ? (
              <CngButton
                color='primary'
                disabled={loading}
                onClick={onSubmitUpdate}
                size='medium'
                shouldShowProgress={loading}
                startIcon={<FontAwesomeIcon icon={['fal', 'paper-plane']} />}
              >
                Submit Update
              </CngButton>
            ) : (
              <CngButton
                color='primary'
                disabled={loading}
                onClick={onSubmitUpdate}
                size='medium'
                shouldShowProgress={loading}
                startIcon={<FontAwesomeIcon icon={['fal', 'paper-plane']} />}
              >
                {recordStatus === 'SUBM_ERR' ? 'Correct & Resubmit' : 'Validate & Submit'}
              </CngButton>
            )}
          </Grid>
        )}
      </Grid>

      <ConfirmDialog
        isConfirmDialogOpen={confirmDialog}
        closeDialog={() => setConfirmDialog(false)}
        confirmDialog={() => history.push(pathMap.UAE_MPCI_HOUSE_BILL_LIST_VIEW)}
        content="All progress in this session will be lost and can't be restored. Are you sure about this?"
        okMsg='Yes, discard'
        cancelMsg='No, continue'
        title='Discard Changes'
      />
    </>
  )
}

// Values must match BE contract: NEW|CXL|CHG|RIR|RIJ|SPL|OSP|SWB
const UPDATE_FUNCTION_TYPES = [
  { text: 'Change or Update Bill of Lading', value: 'CHG' },
  { text: 'Switch Bill of Lading', value: 'SWB' },
  { text: 'Response to an RFI - Information to be provided', value: 'RIR'},
  { text: 'Response to an RFI - Information cannot be provided', value: 'RIJ'},
  
]

function UpdateFilingBanner({ recordStatus, submitMode, setSubmitMode, files, onFileChange, onRemove, fileInputRef, fileUploadErrors, onClearFileErrors }) {
  const { watch, setValue } = useFormContext()
  const functionType = watch('functionType') || ''
  const isRfi = recordStatus === STATUS_CODES.RFI
  const visibleTypes = UPDATE_FUNCTION_TYPES
  const reasonRequired = functionType !== 'RIR'
  const isDocsOnly = submitMode === 'supporting_docs_only'

  const toggleBtnBase = {
    textTransform: 'none',
    fontSize: 13,
    fontWeight: 500,
    padding: '6px 14px',
    borderRadius: 6,
    minWidth: 0
  }
  const activeStyle = { background: '#1a2b4a', color: '#fff', border: '1px solid #1a2b4a' }
  const inactiveStyle = { background: 'transparent', color: '#1a2b4a', border: '1px solid #c8c8c8' }

  return (
    <Box
      mb={2}
      style={{
        padding: '16px 18px',
        background: '#fff8e1',
        border: '1px solid #ffe082',
        borderRadius: 8
      }}
    >
      {/* Header */}
      <Box display='flex' alignItems='center' mb={0.5} style={{ gap: 8 }}>
        <FontAwesomeIcon icon={['fal', 'triangle-exclamation']} style={{ fontSize: 14, flexShrink: 0, color: '#f59e0b' }} />
        <Typography variant='subtitle2'>Update Filing</Typography>
      </Box>
      <Typography variant='caption' color='textSecondary' style={{ display: 'block', marginBottom: 12 }}>
        {isDocsOnly
          ? 'Attach your supporting documents below, then submit to NAIC — no Function Type or Reason needed.'
          : 'Select a Function Type and provide a Reason (when required) before submitting the update to NAIC.'}
      </Typography>

      {/* Toggle */}
      <Typography variant='caption' style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}>
        What would you like to submit?
      </Typography>
      <Box display='flex' style={{ gap: 8, marginBottom: 4 }}>
        <Button
          style={{ ...toggleBtnBase, ...(isDocsOnly ? inactiveStyle : activeStyle) }}
          onClick={() => setSubmitMode('update_filing')}
          startIcon={<FontAwesomeIcon icon={['fal', 'pen-to-square']} style={{ fontSize: 12 }} />}
        >
          Update filing
        </Button>
        <Button
          style={{ ...toggleBtnBase, ...(isDocsOnly ? activeStyle : inactiveStyle) }}
          onClick={() => setSubmitMode('supporting_docs_only')}
          startIcon={<FontAwesomeIcon icon={['fal', 'paperclip']} style={{ fontSize: 12 }} />}
        >
          Supporting documents only
        </Button>
      </Box>
      <Typography variant='caption' color='textSecondary' style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 14 }}>
        <FontAwesomeIcon icon={['fal', 'circle-info']} style={{ fontSize: 11 }} />
        {isDocsOnly
          ? 'Attach at least one document. Function Type and Reason are not required.'
          : 'Change or switch the bill, or respond to an RFI. Function Type and Reason apply.'}
      </Typography>

      <Divider style={{ marginBottom: 16 }} />

      {/* Update filing fields */}
      {!isDocsOnly && (
        <Grid container spacing={2} style={{ marginBottom: 16 }}>
          {/* Function Type */}
          <Grid item xs={12} sm={5} lg={4}>
            <Typography variant='caption' style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>
              Function Type <span style={{ color: '#d32f2f' }}>*</span>
            </Typography>
            <TextField
              select
              fullWidth
              value={functionType}
              size='small'
              variant='outlined'
              onChange={(e) => setValue('functionType', e.target.value, { shouldValidate: true })}
            >
              <MenuItem value=''><em>Select function type...</em></MenuItem>
              {visibleTypes.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.text}</MenuItem>
              ))}
            </TextField>
            <Typography variant='caption' color='textSecondary'>
              Determines the NAIC processing path for this update
            </Typography>
          </Grid>

          {/* Reason For Updating Filing */}
          <Grid item xs={12} sm={7} lg={8}>
            <Typography variant='caption' style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>
              Reason For Updating Filing
              {' '}
              <Chip
                label={reasonRequired ? 'Required' : 'Optional'}
                size='small'
                style={{
                  fontSize: 10,
                  height: 18,
                  color: reasonRequired ? '#d32f2f' : '#757575',
                  background: 'transparent',
                  border: `1px solid ${reasonRequired ? '#d32f2f' : '#bdbdbd'}`,
                  fontWeight: 400
                }}
              />
              {' '}
              <Tooltip title={'Use this field based on the selected Function Type. Change or Update / Switch Bill of Lading / Response to RFI - Info to be provided: Enter a brief explanation of the update or additional details not captured in other fields. Example: Consignee details updated, Invoice Number: 6758658, MOHAP permit no: 68976645. Response to RFI - Info cannot be provided: Provide a clear justification explaining why the requested information cannot be submitted.'}>
                <span style={{ cursor: 'help', pointerEvents: 'auto' }}>
                  <FontAwesomeIcon icon={['fal', 'circle-info']} style={{ fontSize: 11, opacity: 0.6 }} />
                </span>
              </Tooltip>
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              size='small'
              variant='outlined'
              placeholder='Provide a clear explanation for this update...'
              inputProps={{ maxLength: 512 }}
              value={watch('reasonForUpdatingFiling') || ''}
              onChange={(e) => setValue('reasonForUpdatingFiling', e.target.value, { shouldValidate: true })}
            />
            <Typography variant='caption' color='textSecondary'>
              Max 512 chars · Required when Function Type is 'Change or Update Bill' or 'RFI - Information cannot be provided'
            </Typography>
          </Grid>
        </Grid>
      )}

      {/* Supporting Documents */}
      <SupportingDocsUpload
        files={files}
        onFileChange={onFileChange}
        onRemove={onRemove}
        fileInputRef={fileInputRef}
        required={isDocsOnly}
        uploadErrors={fileUploadErrors}
        onClearErrors={onClearFileErrors}
      />
    </Box>
  )
}

function FilingReferenceSection({ recordStatus, billType, systemId }) {
  const { watch } = useFormContext()
  const mpciDocId    = watch('mpciDocId')    || '—'
  const billNo       = watch('billNo')       || '—'
  const parentBillNo = watch('parentBillNo') || '—'
  const vesselName   = watch('vesselName')   || '—'

  return (
    <Box
      mb={2}
      style={{
        border: '1px solid rgba(0,0,0,0.10)',
        borderRadius: 8,
        overflow: 'hidden'
      }}
    >
      <Box
        display='flex'
        alignItems='center'
        style={{
          padding: '10px 16px',
          background: '#fafafa',
          borderBottom: '1px solid rgba(0,0,0,0.07)',
          gap: 8
        }}
      >
        <FontAwesomeIcon icon={['fal', 'file-lines']} style={{ fontSize: 14, opacity: 0.7 }} />
        <Typography variant='subtitle2' style={{ flex: 1 }}>Filing Reference</Typography>
        {recordStatus && (
          <Chip
            label={`Currently: ${getStatusLabel(recordStatus)}`}
            size='small'
            style={{ fontSize: 11, height: 22, fontWeight: 600 }}
          />
        )}
      </Box>

      <Box style={{ padding: '12px 16px' }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant='caption' color='textSecondary' style={{ textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.5 }}>System ID</Typography>
            <Typography variant='body2' style={{ fontWeight: 600, wordBreak: 'break-all' }}>{systemId || '—'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant='caption' color='textSecondary' style={{ textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.5 }}>MPCI Doc ID</Typography>
            <Typography variant='body2' style={{ fontWeight: 600, wordBreak: 'break-all' }}>{mpciDocId}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant='caption' color='textSecondary' style={{ textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.5 }}>Master Bill No</Typography>
            <Typography variant='body2' style={{ fontWeight: 600, wordBreak: 'break-all' }}>{parentBillNo}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant='caption' color='textSecondary' style={{ textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.5 }}>House Bill No</Typography>
            <Typography variant='body2' style={{ fontWeight: 600, wordBreak: 'break-all' }}>{billNo}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant='caption' color='textSecondary' style={{ textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.5 }}>Vessel</Typography>
            <Typography variant='body2' style={{ fontWeight: 600, wordBreak: 'break-all' }}>{vesselName}</Typography>
          </Grid>
        </Grid>
      </Box>
    </Box>
  )
}

function SupportingDocsUpload({ files, onFileChange, onRemove, fileInputRef, required, uploadErrors, onClearErrors }) {
  return (
    <Box>
      <Box display='flex' alignItems='center' mb={1} style={{ gap: 8 }}>
        <FontAwesomeIcon icon={['fal', 'paperclip']} style={{ fontSize: 14 }} />
        <Typography variant='subtitle2'>Supporting Documents</Typography>
        <Chip
          label={required ? 'Required' : 'Optional'}
          size='small'
          style={{
            fontSize: 11,
            height: 20,
            color: required ? '#fff' : undefined,
            background: required ? '#d32f2f' : undefined
          }}
        />
      </Box>

      {/* Upload error messages */}
      {uploadErrors && uploadErrors.length > 0 && (
        <Box mb={1} style={{ background: '#fff3f3', border: '1px solid #f44336', borderRadius: 6, padding: '8px 12px' }}>
          {uploadErrors.map((err, i) => (
            <Typography key={i} variant='caption' style={{ color: '#d32f2f', display: 'block' }}>
              {err}
            </Typography>
          ))}
          <Typography
            variant='caption'
            style={{ color: '#1976d2', cursor: 'pointer', display: 'block', marginTop: 4 }}
            onClick={onClearErrors}
          >
            Dismiss
          </Typography>
        </Box>
      )}

      <Box
        onClick={() => fileInputRef.current && fileInputRef.current.click()}
        display='flex'
        flexDirection='column'
        alignItems='center'
        justifyContent='center'
        style={{
          minHeight: 90,
          border: `2px dashed ${required ? '#f44336' : 'rgba(0,0,0,0.18)'}`,
          borderRadius: 8,
          cursor: 'pointer',
          background: '#fafafa',
          padding: '16px 12px',
          gap: 6
        }}
      >
        <FontAwesomeIcon icon={['fal', 'file-arrow-up']} style={{ fontSize: 22, opacity: 0.55 }} />
        <Typography variant='caption' color='textSecondary'>
          Click to upload or drag &amp; drop
        </Typography>
        <Typography variant='caption' color='textSecondary'>
          PDF, JPG, PNG · Max 10 MB per file · Up to 5 files
        </Typography>
      </Box>
      <input
        ref={fileInputRef}
        type='file'
        multiple
        accept='.pdf,.jpg,.jpeg,.png'
        style={{ display: 'none' }}
        onChange={onFileChange}
      />

      {files.length > 0 && (
        <Box mt={1}>
          {files.map((file, idx) => (
            <Box
              key={idx}
              display='flex'
              alignItems='center'
              style={{
                gap: 8,
                padding: '6px 10px',
                background: '#f5f4f0',
                borderRadius: 6,
                marginBottom: 4
              }}
            >
              <FontAwesomeIcon icon={['fal', 'file']} style={{ fontSize: 13, opacity: 0.6 }} />
              <Typography variant='caption' style={{ flex: 1 }}>{file.name}</Typography>
              <Typography variant='caption' color='textSecondary'>
                {(file.size / 1024).toFixed(0)} KB
              </Typography>
              <IconButton size='small' onClick={() => onRemove(idx)}>
                <FontAwesomeIcon icon={['fal', 'xmark']} style={{ fontSize: 12 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}
